import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/dashboard-admin/AdminSidebar';
import Logo from '../../components/common/Logo';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Search, Download, Loader, CreditCard, FileSpreadsheet } from 'lucide-react';
import PaymentService from '../../services/payment.service';
import * as XLSX from 'xlsx';

const AdminPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showExportOptions, setShowExportOptions] = useState(false);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const data = await PaymentService.getAllPayments();
      setPayments(data);
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Impossibile caricare i pagamenti');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completato';
      case 'pending': return 'In attesa';
      case 'failed': return 'Fallito';
      default: return status;
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      (payment.user?.first_name + ' ' + payment.user?.last_name).toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.appointment?.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.amount.toString().includes(searchTerm);
    
    const matchesFilter = filterStatus === 'all' || payment.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const totalAmount = filteredPayments
    .filter(payment => payment.status === 'completed')
    .reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
    .toFixed(2);

  const exportToCSV = () => {
    const headers = ['Data', 'Paziente', 'Professionista', 'Servizio', 'Importo', 'Stato'];
    
    const csvRows = [
      headers.join(','),
      ...filteredPayments.map(payment => {
        return [
          format(new Date(payment.created_at), 'dd/MM/yyyy', { locale: it }),
          `${payment.user?.first_name} ${payment.user?.last_name}`,
          `${payment.appointment?.therapist?.user?.first_name} ${payment.appointment?.therapist?.user?.last_name}`,
          payment.appointment?.service_name,
          payment.amount,
          getStatusLabel(payment.status)
        ].join(',');
      })
    ];
    
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `pagamenti_${format(new Date(), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  const exportToExcel = () => {
    const headers = ['Data', 'Paziente', 'Professionista', 'Servizio', 'Importo', 'Stato'];
    
    const data = [
      headers,
      ...filteredPayments.map(payment => [
        format(new Date(payment.created_at), 'dd/MM/yyyy HH:mm', { locale: it }),
        `${payment.user?.first_name} ${payment.user?.last_name}`,
        `${payment.appointment?.therapist?.user?.first_name} ${payment.appointment?.therapist?.user?.last_name}`,
        payment.appointment?.service_name,
        payment.amount,
        getStatusLabel(payment.status)
      ])
    ];
    
    // Crea un nuovo workbook
    const wb = XLSX.utils.book_new();
    
    // Crea un worksheet dai dati
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Aggiungi il worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Pagamenti');
    
    // Configura larghezze colonne
    const colWidths = [
      { wch: 16 },  // Data
      { wch: 20 },  // Paziente
      { wch: 20 },  // Professionista
      { wch: 25 },  // Servizio
      { wch: 10 },  // Importo
      { wch: 15 },  // Stato
    ];
    ws['!cols'] = colWidths;
    
    // Genera il file e avvia il download
    XLSX.writeFile(wb, `pagamenti_${format(new Date(), 'yyyyMMdd')}.xlsx`);
    setShowExportOptions(false);
  };

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <AdminSidebar />
      </div>

      <div className="flex-1 p-8 bg-gray-50">
        <header className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Gestione Pagamenti</h1>
              <p className="text-gray-600">Monitoraggio di tutti i pagamenti della piattaforma</p>
            </div>
            <div className="relative">
              <button 
                onClick={() => setShowExportOptions(!showExportOptions)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta
              </button>
              
              {showExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={exportToCSV}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Esporta CSV
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Esporta Excel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Cerca per nome, servizio o importo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border rounded-lg"
              />
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="p-3 border rounded-lg min-w-32"
            >
              <option value="all">Tutti gli stati</option>
              <option value="completed">Completati</option>
              <option value="pending">In attesa</option>
              <option value="failed">Falliti</option>
            </select>
          </div>
        </header>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pagamenti totali</p>
                <p className="text-xl font-semibold">{filteredPayments.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-4">
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Incasso totale</p>
                <p className="text-xl font-semibold">€{totalAmount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 mr-4">
                <CreditCard className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">In attesa</p>
                <p className="text-xl font-semibold">
                  {payments.filter(p => p.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-700">
            {error}
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun pagamento trovato
            </h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? "Nessun pagamento corrisponde ai criteri di ricerca"
                : "Non ci sono ancora pagamenti registrati nel sistema"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paziente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Professionista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Importo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {format(new Date(payment.created_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.user?.first_name} {payment.user?.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.appointment?.therapist?.user?.first_name} {payment.appointment?.therapist?.user?.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.appointment?.service_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        €{payment.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(payment.status)}`}>
                          {getStatusLabel(payment.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentsPage;